import type { ReactNode } from 'react';
import { Link } from '../lib/router';
import { Icon, type IconName } from './Icon';
import s from './List.module.css';

/** A titled group of rows separated by hairlines, for settings and details. */
export function ListGroup({
  title,
  footer,
  children,
}: {
  title?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={s.group}>
      {title && <h3 className={`${s.groupTitle} t-footnote`}>{title}</h3>}
      <div className={s.rows}>{children}</div>
      {footer && <p className={`${s.footer} t-footnote`}>{footer}</p>}
    </section>
  );
}

interface RowProps {
  icon?: IconName;
  title: ReactNode;
  detail?: ReactNode;
  value?: ReactNode;
  to?: string;
  onClick?: () => void;
  trailing?: ReactNode;
  chevron?: boolean;
  danger?: boolean;
}

export function ListRow({
  icon,
  title,
  detail,
  value,
  to,
  onClick,
  trailing,
  chevron,
  danger,
}: RowProps) {
  const content = (
    <>
      {icon && (
        <span className={s.icon}>
          <Icon name={icon} size={20} />
        </span>
      )}
      <span className={s.text}>
        <span className={`${s.title} ${danger ? s.danger : ''}`}>{title}</span>
        {detail && <span className={`${s.detail} t-footnote`}>{detail}</span>}
      </span>
      {value != null && <span className={`${s.value} num`}>{value}</span>}
      {trailing}
      {(chevron ?? !!(to || onClick)) && !trailing && (
        <Icon name="caret-right" size={16} className={s.chevron} />
      )}
    </>
  );
  if (to) {
    return (
      <Link to={to} className={`${s.row} ${s.interactive}`}>
        {content}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`${s.row} ${s.interactive}`}>
        {content}
      </button>
    );
  }
  return <div className={s.row}>{content}</div>;
}
